import {
  BaJwt,
  LoginEventPayload,
  OiamEvents,
  SendTokenPayload,
  SetConfigEventPayload,
  TokenEventPayload,
  isValidBafor,
} from '@oiambk/oiam-oauth-types';
import log from 'loglevel';
import { User } from 'oidc-client-ts';
import { LogoutEventPayload, OauthStates, OiamCookie } from '../model/constants';
import { ERR_LOGIN, ERR_NO_TOKEN, ERR_RELOGIN, OiamErrorPayloadException } from '../model/errors';
import { LoginStep, RequestedLoginStep } from '../model/necessary-login';
import { OiamSessionCookie } from '../model/oiamSessionCookie';
import { checkOwnAndExternalProfile, fulfillsAnyForceActingOnBehalfOfRequirements } from './acr-fns';
import { EServiceConfig } from './eservice-config';
import { FeatureToggle } from './feature-toggle';
import { NginxErrorLogger } from './nginx-error-logger';
import { OauthEventService } from './oauth-event-service';
import { OauthService } from './oauth-service';
import { OidcErrorService } from './oidc-error-service';
import { SessionService } from './session-service';
import { StoreService } from './store-service';
import { stripSpecialQueryparams } from './utils';

export class StateService {
  private sessionService: SessionService;
  private useMotp = false;
  private forceProfileSelectionOnStartup = false;
  private renewTokenErrorTimeout: number = 0;
  private renewTokenErrorCount: number = 0;
  private eServiceConfig: EServiceConfig = new EServiceConfig();

  /**
   * Init all services and deside actions according to state
   */
  constructor() {
    this.sessionService = new SessionService();

    // check for a special query params in the URL
    this.rememberStartupParameters();
  }

  async handleBackForwardCache() {
    let oauthState: OauthStates = StoreService.readState();
    if (await this.isSessionTerminated(oauthState)) {
      return;
    }

    // don't retry auth flows
    if (this.isUnfinishedSessionStart()) {
      await this.clearUnfinishedSessionStart();
    }

    // only if configured
    if (OauthService.checkConfigured()) {
      // check BA-SessionId, oiamsession and old BaLoginSession cookie
      // some conditions lead to authorization code request and redirect here (login, relogin)
      await this.checkCookies(oauthState, { forceProfileSelectionOnCompatibleProfileChange: true });
    } else {
      // page not loaded completely from cache
      // hard reload to prevent empty page
      await OauthEventService.sendBeforeNavigationEventWithWait();
      window.location.reload();
    }

    // always send oiamTokenEvent at browser back
    await this.handleResendTokenState();
  }

  async updateStateAfterVisibleChange() {
    const oauthState: OauthStates = StoreService.readState();
    log.debug('oiam-oauth-wc: updateStateAfterVisibleChange', oauthState);
    if (await this.isSessionTerminated(oauthState)) {
      log.debug('oiam-oauth-wc: session terminated');
      return;
    }

    // check BA-SessionId, oiamsession and old BaLoginSession cookie
    // some conditions lead to authorization code request and redirect here (login, relogin)
    const resendtoken = await this.checkCookies(oauthState, { forceProfileSelectionOnCompatibleProfileChange: true });

    if (resendtoken) {
      // need to resend token
      await this.handleResendTokenState();
    }
  }

  async handleSendSessionEvent() {
    if (this.sessionService) {
      return this.sessionService.sendSessionEvent();
    }
  }

  async handleSendTokenEvent(p: SendTokenPayload) {
    if (p?.forcerenew) {
      // get new token from iam
      await this.handleRenewtokenState();
      // get token failed -> do not send invalid token to eService
      if (this.renewTokenErrorCount > 0) {
        return;
      }
    }
    await this.handleResendTokenState();
  }

  async handleLoginEvent(loginArgs: LoginEventPayload | undefined) {
    if (loginArgs) {
      this.eServiceConfig.setLoginAcrValues(loginArgs);
    }
    await this.handleLoginrequestedState(StoreService.readState());
  }

  async handleLogoutEvent(payload: LogoutEventPayload) {
    if (payload) {
      await this.changeState(payload.reason);
    } else {
      await this.handleLogoutRequestedState();
    }
  }

  async handleSetConfigEvent(setConfigPayload: SetConfigEventPayload) {
    try {
      this.eServiceConfig.setConfigPayload = setConfigPayload;
    } catch {
      // invalid config will not be set -> do not process any further, an error event was already sent
      return;
    }
    await OauthService.setConfig(setConfigPayload);

    const ready = await this.updateStateAfterConfigSet();
    log.debug('oiam-oauth-wc: handleConfiguredEvent ready? ', ready);

    // send only if there are no errors
    if (ready) {
      // event to stop loaded event
      OauthEventService.emitter.emit(OauthEventService.CONFIGURED_EVENT);
      OauthEventService.doDispatchEvent(OiamEvents.READYFORLOGIN);
    } else {
      log.warn('Error while processing SetConfig, oiam-oauth-wc is not ReadyForLogin');
    }
  }

  /**
   * @returns true if oiamReadyForLogin should be sent afterward
   */
  private async updateStateAfterConfigSet() {
    // check error responses in url
    // can be either from authorization code redirects or browser back -> check it independent of state
    const urlSearchParams = new URLSearchParams(window.location.search);
    const oidcErrorService = this.createOidcErrorService();

    if (OidcErrorService.isHandleableError(urlSearchParams.get('error'))) {
      // We wanted to get a code, but have received a more or less expected error instead.
      const errorCheckResult = await this.handleOidcErrors(oidcErrorService, urlSearchParams.get('state'));
      if (errorCheckResult === 'redirect') {
        // Allow some seconds for navigating away. Only return/continue in case the navigation fails (2 seconds timeout)
        await new Promise(resolve => setTimeout(resolve, 2000));
        return false;
      }
    }

    // do we have to resume an auth flow or create a profile first?
    const finishSessionStart = await this.isUnfinishedSessionStart();
    if (finishSessionStart) {
      // Implicit baidmatch?
      if (
        // Keycloak's way to signal that the last profile selection lacks baidmatch,
        // but the login as itself went fine.
        urlSearchParams.has('identity') &&
        isValidBafor(urlSearchParams.get('identity')) &&
        urlSearchParams.has('acting-on-behalf-of') &&
        isValidBafor(urlSearchParams.get('acting-on-behalf-of')) &&
        // If eService requests concrete actingOnBehalfOfs, these will be requested in a later step
        // (this might be a different BAFOR).
        // The empty array is a special signal to prevent implict baidmatch.
        !this.eServiceConfig.acrValues.actingOnBehalfOf
      ) {
        const baidmatchUrl = oidcErrorService.calcBaidmatchUrl(
          urlSearchParams.get('acting-on-behalf-of'),
          urlSearchParams.get('identity'),
          urlSearchParams.get('acting-on-behalf-of'),
        );
        OidcErrorService.redirectTo(baidmatchUrl);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return false;
      }

      const success = await this.handleAuthorizationCodeRequestedState();
      // could not get JWT
      if (!success) {
        return false;
      }
    } else {
      if (this.shouldLoginAutomatically()) {
        await this.changeState(OauthStates.LOGINREQUESTED);
        return;
      }
      if (this.forceProfileSelectionOnStartup) {
        this.eServiceConfig.setLoginAcrValues({ forceProfileSelection: true });
      }
      // check BA-SessionId, oiamsession and old BaLoginSession cookie
      // some conditions lead to authorization code request and redirect here (login, relogin)
      await this.checkCookies(StoreService.readState(), {
        forceProfileSelectionOnCompatibleProfileChange: false,
      });
    }

    // check if we have to unlink clones on eService change
    if (StoreService.readState() === OauthStates.LOGGEDIN) {
      OauthService.unlinkFromClones();
    }

    // token event to communicate current login state
    await this.handleResendTokenState();
    return true;
  }

  private async changeState(toState: any) {
    // prevent null state
    if (!toState) {
      return false;
    }

    // get current state
    let currentState: OauthStates = StoreService.readState();
    log.debug('oiam-oauth-wc: try changeState to ', toState, 'current state is ', currentState);

    switch (toState) {
      case OauthStates.LOGOUTAFTERIDLESESSIONTIMEOUT: {
        // handle redirect from IAM at logout after session expired
        this.handleLogoutAfterIdleSessionTimeoutState();
        break;
      }
      case OauthStates.LOGOUTAFTERMAXSESSIONTIMEOUT: {
        // handle redirect from IAM at logout after session expired
        this.handleLogoutAfterMaxSessionTimeoutState();
        break;
      }
      case OauthStates.LOGINREQUESTED: {
        /*
        start login if not already logged in
         */
        await this.handleLoginrequestedState(currentState);
        break;
      }
      case OauthStates.RELOGINREQUESTED: {
        await this.handleReloginrequestedState('(re-)login');
        break;
      }
      case OauthStates.REAUTHREQUIRED: {
        await this.handleReauthRequiredState();
        break;
      }
      case OauthStates.PROFILESELECTIONREQUESTED: {
        await this.handleReloginrequestedState('profile-selection');
        break;
      }
      case OauthStates.ACCOUNTLINK_REQUESTED: {
        await this.handleReloginrequestedState('account-link');
        break;
      }
      case OauthStates.IDLETIMEDOUT: {
        // start logout if logged in
        await this.handleInternalIdleTimeoutState(currentState);
        break;
      }
      case OauthStates.MAXTIMEDOUT: {
        /*
        start logout if logged in
         */
        await this.handleMaxTimeoutState(currentState);
        break;
      }
      case OauthStates.LOGOUTREQUESTED: {
        // start logout if logged in
        await this.handleLogoutRequestedState();
        break;
      }
      case OauthStates.LOGGEDOUTEXTERNAL: {
        // remove user session if logged in
        await this.handleExternalLoggedoutState(currentState);
        break;
      }
      case OauthStates.RENEWTOKEN: {
        // request a new JWT from IAM and send it as an event
        await this.handleRenewtokenState();
        break;
      }
      case OauthStates.LOGGEDIN: {
        // only one occurrence: cancel inside authorization code flow (profile selection)
        // reset the jwt inside SessionService
        const user = await OauthService.getUser();
        if (user) {
          const baJwt: BaJwt = new BaJwt(user.access_token);
          this.sessionService.setJwt(baJwt);
        }
        // save correct state
        StoreService.saveState(OauthStates.LOGGEDIN);
        // we have to strip the url from oidc query parameters,
        // check previous invocation and send custom state if any
        const prev = StoreService.getPreviousInvocation();
        OauthService.handleUrlAndState(prev?.customstate);
        break;
      }
    }
    return true;
  }

  private async isSessionTerminated(oauthState: OauthStates) {
    if (oauthState === OauthStates.LOGGEDIN) {
      if (this.sessionService.isMaxSessionExpired()) {
        // logout with redirect to IAM
        await this.changeState(OauthStates.MAXTIMEDOUT);
        return true;
      }
      if (this.sessionService.isIdleSessionExpired()) {
        // logout with redirect to IAM
        await this.changeState(OauthStates.IDLETIMEDOUT);
        return true;
      }
      if (this.sessionService.hasSessionBeenTerminated()) {
        await this.changeState(OauthStates.LOGGEDOUTEXTERNAL);
        return true;
      }
    }
    return false;
  }

  private async checkCookies(
    oauthState: OauthStates,
    options: { forceProfileSelectionOnCompatibleProfileChange?: boolean },
  ): Promise<boolean> {
    // check for login/logout in another tab and update idle session from another tab
    if (
      oauthState === OauthStates.EMPTY ||
      oauthState === OauthStates.LOGGEDIN ||
      oauthState === OauthStates.LOGGEDOUTEXTERNAL
    ) {
      return await this.checkOiamSession(options);
    }
    return false;
  }

  // ### Internal State handler #####################################

  private async handleResendTokenState() {
    const jwt = await OauthService.askForToken(false);
    const sendTokenBody: TokenEventPayload = {
      clientid: StoreService.getClientId(),
      jwt: jwt,
    };
    OauthEventService.doDispatchEvent(OiamEvents.TOKEN, sendTokenBody);
  }

  private async handleRenewtokenState() {
    // there is a token check at the session timer
    // if there is an error here this check leads to a new token request each second
    // -> handle errors and ignore calls for some time
    if (this.renewTokenErrorTimeout > 0) {
      this.renewTokenErrorTimeout--;
      return;
    }
    // store new jwt at SessionService for token refresh and check of authn-level change
    const jwt = await OauthService.askForToken(true).catch(async (e: any) => {
      // log at nginx
      NginxErrorLogger.logError({ ...ERR_NO_TOKEN, details: e?.error });

      // retry if no specific error
      if (!e || !e.error) {
        // skip session timer renew token calls for some seconds
        // extend the time at each error (2s, 4s, 6s ...)
        // it depends on "grace time" how often a retry occurs before invalid_grant is returned from keycloak
        this.renewTokenErrorCount++;
        this.renewTokenErrorTimeout = this.renewTokenErrorCount * 2;
      } else {
        // We have to authenticate anew. If we reliably had an up-to-date customstate,
        // we could relogin. But because this is not guaranteed, we "press F5" to re-enter
        // the current eService at the current URL.
        // But before, we throw away all tokens to enforce a relogin at reentry.
        this.renewTokenErrorCount = 0;
        this.renewTokenErrorTimeout = 0;
        await OauthService.removeUser();
        await OauthEventService.sendBeforeNavigationEventWithWait();
        window.location.reload();
      }
      return '';
    });
    if (jwt) {
      this.renewTokenErrorCount = 0;
      this.renewTokenErrorTimeout = 0;
      this.sessionService.setJwt(new BaJwt(jwt));
    }
  }

  private async handleLoginrequestedState(oauthState: OauthStates) {
    if (
      oauthState === OauthStates.EMPTY ||
      oauthState === OauthStates.LOGGEDIN ||
      oauthState === OauthStates.LOGINREQUESTED ||
      oauthState === OauthStates.LOGGEDOUTEXTERNAL
    ) {
      let requestedLoginStep: RequestedLoginStep = '(re-)login';
      // only check jwt if internal state is logged-in
      // in all other cases there is no jwt or the state is stale and must be validated at IAM with new auth code flow
      if (oauthState === OauthStates.LOGGEDIN) {
        requestedLoginStep = await this.decideRequestedLoginStep(this.eServiceConfig);
      }

      // LOGINREQUESTED is a one-time action, clean up to avoid loops
      if (oauthState === OauthStates.LOGINREQUESTED) {
        StoreService.deleteState();
      }

      if (requestedLoginStep !== 'none') {
        await OauthService.doLogin(requestedLoginStep, this.eServiceConfig).catch(err => {
          this.resetAtErrorOrExternalLogout({ ...ERR_LOGIN, details: err });
        });
      } else {
        // protected eServices just send oiamLoginEvent not an oiamSendTokenEvent
        // the oiamTokenEvent is not automatically send if we are already logged in e.g. on reload page
        // resend token for protected eServices at oiamLoginEvent
        await this.handleResendTokenState();
      }
    }
  }

  private async handleReloginrequestedState(loginStep: LoginStep) {
    await OauthService.doLogin(loginStep, this.eServiceConfig).catch(err => {
      this.resetAtErrorOrExternalLogout({ ...ERR_RELOGIN, details: err });
    });
  }

  /**
   * @returns true if the eService can continue to work, be it with a suiting JWT or ReadyForLogin
   *   (maybe after cleaning up an errorneous state).
   *   False if a redirect to profile-selection has been initiated.
   */
  private async handleAuthorizationCodeRequestedState(): Promise<boolean> {
    const urlSearchParams = new URLSearchParams(window.location.search);
    let user: User;

    const prev = StoreService.getPreviousInvocation();
    if (prev) {
      // If there is a previous invocation pending, use its customstate and ACR values.
      // Purpose: when getting directed to profile creation, the original deep link has to be remembered, and
      // when returning from profile creation, the deep link is no longer known (there hasn't been a CustomStateEvent
      // inbetween because there hasn't been a code but an unmet_authentication_requirements from the last OIAM redirect).
      // This customstate might have its specific ACR values attached, thus check against them.

      log.debug('Continuing previous invocation', prev);
      this.eServiceConfig.setLoginAcrValues(prev);
    }

    if (this.eServiceConfig.forcedActingOnBehalfOf?.length > 0) {
      // return from FSCE:
      //  check the forced acting-on-behalf-of against eService requirements
      //  if not matching, redirect to Profil-Online
      if (
        !fulfillsAnyForceActingOnBehalfOfRequirements(
          this.eServiceConfig.forcedActingOnBehalfOf,
          this.eServiceConfig.acrValues.actingOnBehalfOf,
          this.eServiceConfig.acrValues.actingType,
        )
      ) {
        const url = this.calcProfilonlineUrl(this.eServiceConfig.forcedActingOnBehalfOf.join(','));
        OidcErrorService.redirectTo(url);
        // Allow some seconds for navigating away. Only return/continue in case the navigation fails (2 seconds timeout)
        await new Promise(resolve => setTimeout(resolve, 2000));
        return false;
      }
    } else if (!urlSearchParams.get('code')) {
      log.warn(
        'oiam-oauth-wc: Authorization request was sent but after redirect authorization code is missing in query parameters!',
      );
    } else {
      user = await OauthService.getToken().catch(async err => {
        // error at getting tokens -> handle the error
        await this.resetAtErrorOrExternalLogout(OiamErrorPayloadException.extract(err));
        return null;
      });
      if (user) {
        this.sessionService.startSession(new BaJwt(user.access_token));
        // set correct internal state
        StoreService.saveState(OauthStates.LOGGEDIN);
      }
    }

    // check for correct profile
    // missing or wrong profile triggers an authorization code request and thus a redirect
    const continueProcessing = await this.checkProfile();
    if (continueProcessing === 'redirect') {
      return false;
    }

    // remove code, state from query params to prevent errors on relogin, bookmarks etc.
    OauthService.handleUrlAndState((user?.state as LoginEventPayload)?.customstate);

    return true;
  }

  private async handleLogoutRequestedState() {
    // if (oauthState === OauthStates.LOGGEDIN || oauthState === OauthStates.AUTHCODEREQUESTED) {
    // clear session storage to delete all user data for the session (also data stored by eServices)
    // it must not be any user data available after session is logged out.
    // We have to delete now even though Keycloak might choose to display a logout confirmation page.
    // Clearing on come back is error-prone since the newly entered eService (like https://www.arbeitsagentur.de/)
    // could already have written entries into sessionStorage which shouldn't be deleted when
    // entering this page. Therefore we have to clear before leaving.
    StoreService.deleteUserData(false);
    this.sessionService.stopSession(false);
    return OauthService.doLogout().catch(err => {
      this.resetAtErrorOrExternalLogout(OiamErrorPayloadException.extract(err));
    });
  }

  private async handleReauthRequiredState() {
    // logout, but keep retry invocation
    StoreService.deleteUserData(true);
    this.sessionService.stopSession(true);
    StoreService.copyRetryToPreviousInvocation();
    StoreService.saveState(OauthStates.LOGINREQUESTED);

    // set logoutredirecturi to redirecturi to immediately cause a login afterwards
    const updatedConfig: SetConfigEventPayload = {
      ...this.eServiceConfig.setConfigPayload,
      logouturi: this.eServiceConfig.setConfigPayload.redirecturi,
    };
    await OauthService.setConfig(updatedConfig);
    return OauthService.doLogout().catch(err => {
      this.resetAtErrorOrExternalLogout(OiamErrorPayloadException.extract(err));
    });
  }

  private async handleMaxTimeoutState(oauthState: OauthStates) {
    if (oauthState === OauthStates.LOGGEDIN) {
      this.sessionService.stopSession(false);
      // clear session storage to delete all user data for the session (also data stored by eServices)
      // it must not be any user data available after session is logged out
      StoreService.deleteUserData(false);
      // after logout we emit an max session timed out event so we have to know that state
      // to emit the event also after redirect to default logouturi www.arbeitsagentur.de we save at oiamsession event
      StoreService.setAdditionalInfoInOiamSessionCookie('loggedout-max-timeout');
      // actually we do not need a logout at max session time out (the session times out at server)
      // but:
      // - only a logout deletes the BA-SessionId cookie so the state in browser is clean and dispatched to all other BA tabs
      await OauthService.doLogout();
    }
  }

  private async handleInternalIdleTimeoutState(oauthState: OauthStates) {
    if (oauthState === OauthStates.LOGGEDIN) {
      this.sessionService.stopSession(false);
      // clear session storage to delete all user data for the session (also data stored by eServices)
      // it must not be any user data available after session is logged out
      StoreService.deleteUserData(false);
      // after logout we emit an idle session timed out event so we have to know that state
      // to emit the event also after redirect to default logouturi www.arbeitsagentur.de we save at oiamsession event
      StoreService.setAdditionalInfoInOiamSessionCookie('loggedout-idle-timeout');
      await OauthService.doLogout();
    }
  }

  private async handleLogoutAfterIdleSessionTimeoutState() {
    StoreService.deleteState();
    this.dispatchIdleSessionExpired();
  }

  private dispatchIdleSessionExpired() {
    if (FeatureToggle.supportDeprecatedExpireEvents()) {
      OauthEventService.doDispatchEvent(OiamEvents.IDLESESSIONEXPIRED);
    } else {
      OauthEventService.doDispatchEvent(OiamEvents.IDLESESSIONEXPIRATION);
    }
  }

  private async handleLogoutAfterMaxSessionTimeoutState() {
    StoreService.deleteState();
    OauthEventService.doDispatchEvent(OiamEvents.MAXSESSIONEXPIRATION);
  }

  private async handleOidcErrors(oidcErrorService: OidcErrorService, state: string): Promise<'continue' | 'redirect'> {
    const oidcError = oidcErrorService.checkForOidcErrors();
    if (typeof oidcError === 'boolean') {
      return 'continue';
    }
    OauthService.removePendingCodeFlow(state); // we know, what to do and it is no getToken
    if ('url' in oidcError) {
      // we have to redirect to profile creation or similar, login isn't completed yet
      log.debug('profile to be created/linked', oidcError.url);
      OidcErrorService.redirectTo(oidcError.url);
      return 'redirect'; // Redirection takes time, as the caller, prevent further processing!
    } else if ('targetState' in oidcError) {
      await this.changeState(oidcError.targetState);
      return 'continue'; // no real error
    } else if ('id' in oidcError) {
      // send event to eService
      OauthEventService.doDispatchEvent(OiamEvents.ERROR, oidcError);

      StoreService.deletePreviousInvocation();
      StoreService.deleteRetryInvocation();
      return 'continue';
    }
  }

  private createOidcErrorService() {
    const profileSelectionRedirectUrls = OauthService.getProfileSelectionRedirectUrls();
    const tdaUrl = this.eServiceConfig.tdaUrl;
    const redirecturi = OauthService.getRedirecturi();
    // Check for errors from OIAM in the URL
    const oidcErrorService = new OidcErrorService(
      StoreService.getAuthority(),
      profileSelectionRedirectUrls,
      redirecturi,
      tdaUrl,
      window.location.href,
    );
    return oidcErrorService;
  }

  // ### external state handler ########################################

  private async handleExternalLoggedoutState(oauthState: OauthStates) {
    if (oauthState === OauthStates.LOGGEDIN) {
      await this.resetAtErrorOrExternalLogout();
    }
    StoreService.deleteState();
  }

  /**
   * Checks for special query parameters that trigger an action on startup
   * - `motp` which contains a one-time login token. If present, use it for logging in immediately.
   * - `force-profile-selection` which triggers an immediate forced profile selection
   * These query parameters get read destructively, i.e. they get removed after storing them
   * in suiting attributes.
   */
  private rememberStartupParameters() {
    const url = new URL(window.location.href);
    const motp = url.searchParams.get('motp');
    if (motp) {
      OauthService.setLoginMotp(motp);
      url.searchParams.delete('motp');
      window.history.replaceState(null, '', url);
    }
    const force = url.searchParams.get('force-profile-selection');
    if (force) {
      this.forceProfileSelectionOnStartup = true;
      url.searchParams.delete('force-profile-selection');
      window.history.replaceState(null, '', url);
    }
    this.useMotp = !!motp;

    // Explicitly does not remove acting-on-behalf-of, is also needed for baidmatch in combination with
    // unmet_authentication_requirements.
    // In that case, setting forcedActingOnBehalfOf is ignored, because there will be a redirect,
    // also ignored at the baidmatch page (identified by parameter identity)
    // no authentication request as the follow-up.
    if (
      !url.searchParams.has('identity') &&
      url.searchParams.has('acting-on-behalf-of') &&
      url.searchParams.get('acting-on-behalf-of')
    ) {
      this.eServiceConfig.forcedActingOnBehalfOf = url.searchParams
        .get('acting-on-behalf-of')
        .split(',')
        .filter(isValidBafor);
    }
  }

  // ### error handler ############################################

  private async resetAtErrorOrExternalLogout(err?) {
    // dispatch error if exists
    if (err) {
      // log at nginx
      NginxErrorLogger.logError(OiamErrorPayloadException.extract(err));
      // send event for eService
      OauthEventService.doDispatchEvent(OiamEvents.ERROR, OiamErrorPayloadException.extract(err));
    }
    await this.cleanUpLocal();
    // send empty token to applications
    const sendTokenBody: TokenEventPayload = {
      clientid: StoreService.getClientId(),
      jwt: '',
    };
    OauthEventService.doDispatchEvent(OiamEvents.TOKEN, sendTokenBody);
  }

  private async cleanUpLocal() {
    // reset user in oidc-client-ts
    await OauthService.removeUser();
    // reset user session
    this.sessionService.stopSession(false);
    // remove any user data
    StoreService.deleteUserData(false);
  }

  // ### handle messages from other tabs ###################

  /**
   * Mega-method to check if the session as a whole and this eService are a match
   * or if the session has to be altered to match (login, relogin, different profile, ...)
   * @returns true if a new token has been requested and shall be sent as an event
   */
  private async checkOiamSession(
    options: { forceProfileSelectionOnCompatibleProfileChange?: boolean } = {},
  ): Promise<boolean> {
    let resendtoken;

    const user = await OauthService.getUser();
    const parsedSessionCookie: OiamSessionCookie | undefined = StoreService.getOiamSessionCookie();
    const baJwt: BaJwt | undefined = (user?.access_token && new BaJwt(user?.access_token)) || undefined;

    // Resend token on visibility change, if there was a token refresh in another tab with the same clientid.
    // Check is based on the jwt got from token storage and the (possible old) jwt attribute in the session service.
    // We have to do the check here, because "checkForValidSession" resets the jwt inside the session service.
    resendtoken = this.sessionService.haveToResendTokenOnActivating(baJwt);

    const action = this.sessionService.checkForValidSession(baJwt, parsedSessionCookie);
    log.debug('oiam-oauth-wc: Session check finished', action);

    switch (action) {
      case 'idletimeout': {
        await this.changeState(OauthStates.LOGOUTAFTERIDLESESSIONTIMEOUT);
        break;
      }
      case 'maxtimeout': {
        await this.changeState(OauthStates.LOGOUTAFTERMAXSESSIONTIMEOUT);
        break;
      }
      case 'cleanup': {
        // remove stale user in oidc-client-ts
        if (user) {
          await OauthService.removeUser();
        }
        StoreService.deleteState();
        // need to resend (empty) token (done in updateAfterConfigSet, updateAfterVisibilityChange)
        resendtoken = true;
        break;
      }
      case 'cleanup-after-logout': {
        // remove user and state
        // we might have an origin change at logout,
        // so the clear done before has no effect of the logout page
        if (user) {
          await OauthService.removeUser();
          StoreService.deleteState();
        }
        StoreService.deleteOiamCookie(OiamCookie.SESSIONCOOKIE);
        break;
      }
      case 'login': {
        // redirect needs time; do not resend an old token in between
        resendtoken = false;
        await this.changeState(OauthStates.LOGINREQUESTED);
        break;
      }
      case 'relogin': {
        // redirect needs time; do not resend an old token in between
        resendtoken = false;
        await this.changeState(OauthStates.RELOGINREQUESTED);
        break;
      }
      case 'session-valid': {
        await this.ensureLoggedIn();
        if (!baJwt.hasAtLeastAuthnLevel(this.eServiceConfig.acrValues.authnLevel)) {
          // redirect needs time; do not resend an old token in between
          resendtoken = false;
          await this.changeState(OauthStates.RELOGINREQUESTED);
          break;
        }
        // Session is still valid, no stepup needed. Now look at the content/profile of the JWT!
        const profileAction = checkOwnAndExternalProfile({
          ownCurrent: {
            actingType: baJwt?.payload?.['acting-type'],
            actingOnBehalfOf: baJwt?.payload?.['acting-on-behalf-of'],
          },
          externalCurrent: {
            actingType: parsedSessionCookie.actingType,
            actingOnBehalfOf: parsedSessionCookie.actingOnBehalfOf,
          },
          ownRequested: {
            ...this.eServiceConfig.acrValues,
            forcedActingOnBehalfOf: this.eServiceConfig.forcedActingOnBehalfOf,
          },
        });
        log.debug('oiam-oauth-wc: profile check finished', profileAction);
        switch (profileAction) {
          case 'irrelevant': {
            await this.changeState(OauthStates.RENEWTOKEN);
            resendtoken = true;
            break;
          }
          case 'incompatible': {
            // redirect needs time; do not resend an old token in between
            resendtoken = false;
            await this.changeState(OauthStates.PROFILESELECTIONREQUESTED);
            break;
          }
          case 'compatible': {
            if (options.forceProfileSelectionOnCompatibleProfileChange) {
              this.eServiceConfig.mergeLoginAcrValues({ forceProfileSelection: true });
              // redirect needs time; do not resend an old token in between
              resendtoken = false;
              await this.changeState(OauthStates.PROFILESELECTIONREQUESTED);
            } else {
              await this.changeState(OauthStates.RENEWTOKEN);
              resendtoken = true;
            }
            break;
          }
          case 'force': {
            this.eServiceConfig.mergeLoginAcrValues({ forceProfileSelection: true });
            // redirect needs time; do not resend an old token in between
            resendtoken = false;
            await this.changeState(OauthStates.PROFILESELECTIONREQUESTED);
            break;
          }
          case 'none': {
            // Now that we know there is no reason outside of the JWT to renew it,
            // we have to check whether it is still valid.
            // TODO in BaJwt auslagern
            const tokenExpired = baJwt.expirationDate * 1000 < new Date().getTime();
            if (tokenExpired) {
              log.debug('oiam-oauth-wc: token expired -> renew');
              await this.changeState(OauthStates.RENEWTOKEN);
              resendtoken = true;
              break;
            }
          }
        }
        break;
      }
    }

    return resendtoken;
  }

  /**
   * Combination of logout_requested and browser back can lead to an erased state with a valid session.
   * Restore this state if needed.
   */
  private async ensureLoggedIn() {
    const currentState = StoreService.readState();
    if (currentState != OauthStates.LOGGEDIN) {
      log.debug('StateService, session-valid, but currentState=', currentState);
      StoreService.saveState(OauthStates.LOGGEDIN);
    }
  }

  /**
   * Once we have a JWT, we can compare whether it is enough or we need a different profile.
   */
  private async checkProfile(): Promise<'redirect' | 'stay'> {
    const sessionid = StoreService.checkForOiamSessionIdCookie();
    // we are at the end of authorization-code-requested state after token request
    // if everything is ok we have a valid session
    if (sessionid) {
      // Check if a profile has to be selected, or we have to step-up
      const nextStep = await this.decideRequestedLoginStep(this.eServiceConfig);
      if (nextStep === 'none') {
        return 'stay';
      } else {
        const nextState = this.nextStepToState(nextStep);
        await this.changeState(nextState);
        return 'redirect';
      }
    }
    return 'stay'; // error state without redirecting
  }

  private nextStepToState(nextStep: LoginStep): OauthStates {
    switch (nextStep) {
      case 'profile-selection':
        return OauthStates.PROFILESELECTIONREQUESTED;
      case 'account-link':
        return OauthStates.ACCOUNTLINK_REQUESTED;
      case '(re-)login':
        return OauthStates.RELOGINREQUESTED;
    }
  }

  private async decideRequestedLoginStep(eServiceConfig: EServiceConfig): Promise<RequestedLoginStep> {
    const user = await OauthService.getUser();
    if (!user) {
      return '(re-)login';
    }
    if (eServiceConfig.accountLink) {
      return 'account-link';
    }
    const baJwt: BaJwt = new BaJwt(user.access_token);
    if (!baJwt.hasAtLeastAuthnLevel(eServiceConfig.acrValues?.authnLevel)) {
      return '(re-)login';
    }
    if (!baJwt.hasMatchingAuthnMethod(eServiceConfig.acrValues?.authnMethod)) {
      return '(re-)login';
    }
    if (
      !baJwt.fulfills(eServiceConfig.acrValues) ||
      eServiceConfig.acrValues.forceProfileSelection ||
      !baJwt.hasMatchingActingOnBehalfOf(eServiceConfig.forcedActingOnBehalfOf)
    ) {
      return 'profile-selection';
    }
    return 'none';
  }

  private calcProfilonlineUrl(actingOnBehalfOf: string): URL {
    const profileSelectionRedirectUrls = OauthService.getProfileSelectionRedirectUrls();
    const urlPattern =
      profileSelectionRedirectUrls[StoreService.getAuthority()]?.profilonline ??
      profileSelectionRedirectUrls.default.profilonline;
    const url = urlPattern.replace('${ACTING_ON_BEHALF_OF}', actingOnBehalfOf);
    return new URL(url);
  }

  private async isAuthCodeFlowPending(): Promise<boolean> {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (!urlSearchParams.has('state')) {
      return false;
    }
    const state = urlSearchParams.get('state');
    const pending = await OauthService.isPendingCodeFlow(state);
    return pending;
  }

  /**
   * In case of an unfinished authentication code flow or when there are query parameters
   * which force a change in the current profile or when there is a previous invocation
   * pending, the caller has to complete the session start.
   * @returns true if we have to complete the session start (maybe by obtaining a JWT
   * or by checking the current JWT whether it matches all requirement) or we have to
   * redirect away.
   */
  private async isUnfinishedSessionStart(): Promise<boolean> {
    const sessionid = StoreService.checkForOiamSessionIdCookie();
    const prev = StoreService.getPreviousInvocation();
    if (prev && sessionid) {
      // without sessionid, we have to start anew, thus no continuation of a SessionStart
      return true;
    }
    if (this.eServiceConfig.forcedActingOnBehalfOf?.length > 0) {
      return true;
    }
    if (await this.isAuthCodeFlowPending()) {
      return true;
    }
    return false;
  }

  private async clearUnfinishedSessionStart() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const state = urlSearchParams.get('state');
    await OauthService.removePendingCodeFlow(state);
    StoreService.deletePreviousInvocation();
    StoreService.deleteRetryInvocation();
    const strippedUrl = stripSpecialQueryparams(window.location.href, {});
    log.debug('Stripped query parameter: ' + strippedUrl);
    const u = new URL(strippedUrl);
    window.history.replaceState(null, '', u);
  }

  private shouldLoginAutomatically(): boolean {
    if (this.useMotp) {
      return true;
    }
    const sessionid = StoreService.checkForOiamSessionIdCookie();
    if (sessionid) {
      return false;
    }
    const state = StoreService.readState();
    if (state === OauthStates.LOGINREQUESTED) {
      log.debug(`oiam-oauth-wc: trigger login because of state ${state}`);
      return true;
    }
    return false;
  }
}
